import { Button } from "ui/button/button";
import { AsyncButtonIcon } from "ui/button/loading-button/loading-button-icon";
import {
  LOADING_BUTTON_SX,
  LoadingButtonFade,
} from "ui/button/loading-button/loading-button.styles";
import { useStateTransition } from "ui/button/loading-button/use-state-transition";
import {
  type AsyncButtonProps,
  LoadingButtonState,
} from "ui/button/loading-button/loading-button.types";
import { mergeSx } from "ui/merge-sx";

export const LoadingButton = ({
  state = LoadingButtonState.Default,
  loadingMessage,
  successMessage,
  errorMessage,
  loadingIcon,
  successIcon,
  errorIcon,
  successDuration,
  errorDuration,
  color,
  disabled,
  sx,
  ref,
  children,
  ...props
}: AsyncButtonProps) => {
  const { operationState, isTransitioning, iconPhase } = useStateTransition(
    state,
    successDuration,
    errorDuration,
  );

  const renderDynamicButton = () => {
    const contentMap = {
      [LoadingButtonState.Default]: children,
      [LoadingButtonState.Loading]: loadingMessage,
      [LoadingButtonState.Success]: successMessage,
      [LoadingButtonState.Error]: errorMessage,
    };

    const content = contentMap[operationState];

    const iconMap = {
      [LoadingButtonState.Default]: null,
      [LoadingButtonState.Loading]: loadingIcon,
      [LoadingButtonState.Success]: successIcon,
      [LoadingButtonState.Error]: errorIcon,
    };

    const currentIcon = iconMap[operationState];

    const showIcon =
      operationState !== LoadingButtonState.Default &&
      (currentIcon !== null || currentIcon === undefined);

    return (
      <>
        {showIcon && (
          <LoadingButtonFade isFaded={isTransitioning}>
            <AsyncButtonIcon
              state={operationState}
              iconPhase={iconPhase}
              loadingIcon={loadingIcon}
              successIcon={successIcon}
              errorIcon={errorIcon}
            />
          </LoadingButtonFade>
        )}
        {content && <LoadingButtonFade isFaded={isTransitioning}>{content}</LoadingButtonFade>}
      </>
    );
  };

  return (
    <Button
      {...props}
      ref={ref}
      disabled={
        disabled ||
        operationState === LoadingButtonState.Loading ||
        operationState === LoadingButtonState.Success
      }
      color={operationState === LoadingButtonState.Error ? "error" : color}
      sx={mergeSx(LOADING_BUTTON_SX, sx)}>
      {renderDynamicButton()}
    </Button>
  );
};
